import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, money } from "../../lib/api";
import { toast } from "sonner";
import { SupplierAutocomplete } from "../SupplierAutocomplete";
import {
  Plus, X, Loader2, Package, Receipt, Camera, ShoppingBag, CheckCircle2,
  Search, Sparkles,
} from "lucide-react";

const inputCls = "w-full rounded-md border border-slate-300 px-2 py-1 text-xs focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none";

const STATUS_STYLES = {
  pending_pm:            { cls: "bg-amber-100 text-amber-800 border-amber-200",   label: "Pending PM" },
  pending_purchasing:    { cls: "bg-blue-100 text-blue-800 border-blue-200",       label: "Pending Purchasing" },
  approved_purchasing:   { cls: "bg-blue-100 text-blue-800 border-blue-200",       label: "Approved (Buy)" },
  pending_director:      { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", label: "Pending Director" },
  purchased:             { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Purchased" },
  received:              { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Received" },
  rejected:              { cls: "bg-rose-100 text-rose-700 border-rose-200",       label: "Rejected" },
  cancelled:             { cls: "bg-slate-100 text-slate-600 border-slate-200",   label: "Cancelled" },
};

const errMsg = (e, f = "Failed") => {
  const d = e?.response?.data?.detail;
  if (!d) return f;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  return JSON.stringify(d);
};

export default function MaterialRequestsTab({ project, user }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(null);   // "material_request" | "small_purchase" | null
  const [kindFilter, setKindFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get(`/material-requests?project_id=${project.id}`)
      .then(setList).catch(() => setList([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [project.id]);

  const filtered = useMemo(() => {
    if (kindFilter === "all") return list;
    return list.filter((r) => (r.kind || "material_request") === kindFilter);
  }, [list, kindFilter]);

  const totalOpen = useMemo(() =>
    list.filter((r) => !["rejected", "cancelled", "received"].includes(r.status))
        .reduce((s, r) => s + (parseFloat(r.estimated_total) || 0), 0),
    [list]);
  const totalReceived = useMemo(() =>
    list.filter((r) => r.status === "received")
        .reduce((s, r) => s + (parseFloat(r.estimated_total) || 0), 0),
    [list]);

  return (
    <div className="space-y-3" data-testid="prj-mr-tab">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Total requests" value={list.length} color="text-slate-900" />
        <StatCard label="Open value" value={money(totalOpen)} color="text-amber-700" />
        <StatCard label="Received value" value={money(totalReceived)} color="text-emerald-700" />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1">
          {[["all", "All"], ["material_request", "Material requests"], ["small_purchase", "Receipts"]].map(([k, l]) => (
            <button key={k} onClick={() => setKindFilter(k)}
              data-testid={`prj-mr-filter-${k}`}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${kindFilter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            >{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="prj-mr-new-material"
            onClick={() => setShowNew("material_request")}
            className="text-xs font-bold px-3 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white inline-flex items-center gap-1.5"
          ><Plus className="w-3.5 h-3.5" /> New material request</button>
          <button
            data-testid="prj-mr-new-small"
            onClick={() => setShowNew("small_purchase")}
            className="text-xs font-bold px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1.5"
          ><Receipt className="w-3.5 h-3.5" /> Register receipt</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 py-10"><Loader2 className="inline w-4 h-4 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-lg">
          No requests yet — create a material request when something is missing on-site or a small purchase for petty-cash receipts.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
            <div className="col-span-2">#</div>
            <div className="col-span-2">Phase / Scope</div>
            <div className="col-span-3">Items</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Requested</div>
          </div>
          {filtered.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-slate-100 text-xs hover:bg-slate-50/50" data-testid={`prj-mr-row-${r.id}`}>
              <div className="col-span-2 min-w-0">
                <div className="font-mono text-[11px] font-semibold truncate">{r.number}</div>
                <div className="text-[10px] text-slate-500 uppercase font-bold">
                  {(r.kind || "material_request") === "small_purchase"
                    ? <span className="inline-flex items-center gap-1"><Receipt className="w-3 h-3" /> Receipt</span>
                    : <span className="inline-flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Request</span>}
                </div>
              </div>
              <div className="col-span-2 min-w-0">
                <div className="truncate">{r.phase_name || "—"}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.scope_name || ""}</div>
              </div>
              <div className="col-span-3 min-w-0">
                <div className="truncate">{(r.items || []).slice(0, 2).map((i) => i.description).join(", ")}</div>
                <div className="text-[10px] text-slate-400">
                  {(r.items || []).length} item(s)
                  {(r.items || []).some((it) => it.receipt_photo) && (
                    <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 font-semibold">
                      <Camera className="w-3 h-3" /> receipt uploaded
                    </span>
                  )}
                </div>
              </div>
              <div className="col-span-1 text-right font-semibold tabular-nums">{money(r.estimated_total)}</div>
              <div className="col-span-2">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${STATUS_STYLES[r.status]?.cls || "bg-slate-100"}`}>
                  {STATUS_STYLES[r.status]?.label || r.status}
                </span>
                {r.urgency === "urgent" && (
                  <span className="ml-1 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">urgent</span>
                )}
              </div>
              <div className="col-span-2 text-right">
                <div className="text-[10px] text-slate-500">{r.requested_by_name}</div>
                <div className="text-[10px] text-slate-400 tabular-nums">{r.created_at?.slice(0, 10)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-slate-400 italic">
        Tip: <b>Material requests</b> go through PM approval before purchasing acts on them.
        <b> Small purchases</b> (petty cash, on-site emergency) skip PM approval and flow straight to Purchasing for reimbursement/receipt tracking.
        {list.length > 0 && " Click a row (list view coming) or open the request in the Material Requests page for the full workflow."}
      </div>

      {showNew === "material_request" && (
        <NewMaterialRequestModal
          project={project}
          onClose={() => setShowNew(null)}
          onDone={() => { setShowNew(null); load(); }}
        />
      )}
      {showNew === "small_purchase" && (
        <NewReceiptModal
          project={project}
          onClose={() => setShowNew(null)}
          onDone={() => { setShowNew(null); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="text-[10px] font-bold uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${color} tabular-nums mt-0.5`}>{value}</div>
    </div>
  );
}

/* ================= Materials catalog autocomplete ================= */
function MaterialAutocomplete({ value, onChange, onPick, placeholder = "Type to search catalog...", testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = (q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (!q.trim()) { setSuggestions([]); return; }
      setLoading(true);
      try {
        const res = await api.get(`/materials/catalog?q=${encodeURIComponent(q)}&limit=8`);
        setSuggestions(Array.isArray(res) ? res : []);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 200);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => { onChange(e.target.value); fetchSuggestions(e.target.value); setOpen(true); }}
          onFocus={() => { if (value) { fetchSuggestions(value); setOpen(true); } }}
          placeholder={placeholder}
          className="w-full rounded border border-slate-300 pl-6 pr-2 py-1 text-xs focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
        />
        {loading && <Loader2 className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map((s) => (
            <button key={s._id}
              type="button"
              onClick={() => { onPick(s); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
            >
              <div className="font-semibold text-slate-900 truncate">{s.name}</div>
              <div className="text-[10px] text-slate-500 flex gap-2 flex-wrap">
                <span>bought {s.count}×</span>
                {s.last_supplier && <span>· last: {s.last_supplier}</span>}
                {s.trade && <span>· {s.trade}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================= New Material Request (no price, autocomplete) ================= */
function NewMaterialRequestModal({ project, onClose, onDone }) {
  const [phaseId, setPhaseId] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 1, unit: "unit" }]);
  const [busy, setBusy] = useState(false);

  const phases = project.phases || [];
  const scopes = (project.scopes || []).filter((s) => !phaseId || s.phase_id === phaseId);

  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, { description: "", quantity: 1, unit: "unit" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const pickFromCatalog = (i, mat) => {
    updateItem(i, { description: mat.name, unit: "unit" });
  };

  const submit = async () => {
    const clean = items
      .filter((it) => it.description.trim() && (parseFloat(it.quantity) || 0) > 0)
      .map((it) => ({
        description: it.description.trim(),
        quantity: parseFloat(it.quantity),
        unit: it.unit || "unit",
        estimated_price: 0,   // ← purchasing fills this later
        notes: "",
      }));
    if (clean.length === 0) { toast.error("Add at least one valid item"); return; }
    setBusy(true);
    try {
      await api.post("/material-requests", {
        project_id: project.id, kind: "material_request",
        phase_id: phaseId || null, scope_id: scopeId || null,
        urgency, notes, photos: [], items: clean,
      });
      toast.success("Material request submitted");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="prj-mr-new-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2"><Package className="w-4 h-4" /> New material request</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">Item description, unit and quantity only. Purchasing will fill in prices when quoting.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Phase</label>
              <select value={phaseId} onChange={(e) => { setPhaseId(e.target.value); setScopeId(""); }}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs" data-testid="prj-mr-phase">
                <option value="">— none —</option>
                {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Scope (optional)</label>
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} disabled={scopes.length === 0}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs" data-testid="prj-mr-scope">
                <option value="">— none —</option>
                {scopes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Urgency</label>
              <select value={urgency} onChange={(e) => setUrgency(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs">
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select></div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Items</div>
            <div className="border border-slate-200 rounded overflow-visible">
              <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
                <div className="col-span-7">Description</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-2">Unit</div>
                <div className="col-span-1"></div>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b border-slate-100 items-center" data-testid={`prj-mr-item-${i}`}>
                  <div className="col-span-7">
                    <MaterialAutocomplete
                      testid={`prj-mr-item-desc-${i}`}
                      value={it.description}
                      onChange={(v) => updateItem(i, { description: v })}
                      onPick={(mat) => pickFromCatalog(i, mat)}
                      placeholder="Search catalog or type a new item..."
                    />
                  </div>
                  <input type="number" step="0.01" min="0"
                    data-testid={`prj-mr-item-qty-${i}`}
                    className="col-span-2 rounded border border-slate-300 px-1.5 py-1 text-xs text-right tabular-nums"
                    value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} />
                  <input className="col-span-2 rounded border border-slate-300 px-1.5 py-1 text-xs"
                    value={it.unit || "unit"} onChange={(e) => updateItem(i, { unit: e.target.value })}
                    placeholder="sheet / lb / ea" />
                  <button onClick={() => removeItem(i)} disabled={items.length === 1}
                    className="col-span-1 p-1 text-rose-500 rounded hover:bg-rose-50 disabled:opacity-30"
                  ><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <button onClick={addItem} data-testid="prj-mr-add-item"
              className="mt-1 text-[11px] font-semibold text-blue-600 hover:underline inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add item
            </button>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Notes (why is this needed?)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2"
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
              placeholder="e.g. Ran out of drywall screws on-site, needed for AVB phase" />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="prj-mr-submit"
            onClick={submit} disabled={busy}
            className="text-xs px-4 py-1.5 rounded bg-slate-900 hover:bg-slate-800 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
            Submit for approval
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= Register Receipt (small purchase — money already spent) ================= */
function NewReceiptModal({ project, onClose, onDone }) {
  const [phaseId, setPhaseId] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [supplier, setSupplier] = useState("");
  const [purchasedDate, setPurchasedDate] = useState(new Date().toISOString().slice(0, 10));
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [items, setItems] = useState([{ description: "", quantity: 1, unit: "unit", estimated_price: 0 }]);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const phases = project.phases || [];
  const scopes = (project.scopes || []).filter((s) => !phaseId || s.phase_id === phaseId);
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.estimated_price) || 0), 0);
  const total = subtotal + (parseFloat(tax) || 0);
  const aiTotal = scanResult ? parseFloat(scanResult.total || 0) : null;
  const mismatch = aiTotal !== null && Math.abs(aiTotal - total) > 0.05;

  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, { description: "", quantity: 1, unit: "unit", estimated_price: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6);
    const b64s = await Promise.all(files.map((f) => new Promise((r) => {
      const rd = new FileReader();
      rd.onload = () => r(rd.result);
      rd.readAsDataURL(f);
    })));
    setPhotos((p) => [...p, ...b64s].slice(0, 6));
  };

  const scanWithAI = async () => {
    if (photos.length === 0) { toast.error("Attach at least one receipt photo first"); return; }
    setScanning(true);
    try {
      const res = await api.post("/purchasing/scan-receipt-b64", { photos });
      setScanResult(res);
      if (res.supplier) setSupplier(res.supplier);
      if (res.date) setPurchasedDate(res.date);
      if (res.tax != null) setTax(res.tax);
      if (Array.isArray(res.items) && res.items.length > 0) {
        setItems(res.items.map((it) => ({
          description: it.description || "",
          quantity: parseFloat(it.quantity) || 1,
          unit: "unit",
          estimated_price: parseFloat(it.unit_price) || 0,
        })));
      }
      toast.success(`AI extracted ${res.items?.length || 0} item(s) — review before saving`);
    } catch (e) { toast.error(errMsg(e, "AI scan failed")); }
    finally { setScanning(false); }
  };

  const submit = async () => {
    if (photos.length === 0) { toast.error("Attach at least one receipt photo"); return; }
    const clean = items.filter((it) => it.description.trim() && (parseFloat(it.quantity) || 0) > 0)
      .map((it) => ({
        description: it.description.trim(),
        quantity: parseFloat(it.quantity),
        unit: it.unit || "unit",
        estimated_price: parseFloat(it.estimated_price) || 0,
      }));
    if (clean.length === 0) { toast.error("Add at least one line item"); return; }
    setBusy(true);
    try {
      await api.post("/material-requests", {
        project_id: project.id, kind: "small_purchase",
        phase_id: phaseId || null, scope_id: scopeId || null,
        supplier, purchased_date: purchasedDate, tax: parseFloat(tax) || 0,
        notes, photos, items: clean,
      });
      toast.success("Receipt registered");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="prj-mr-new-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-semibold inline-flex items-center gap-2"><Receipt className="w-4 h-4" /> Register receipt</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">Money already spent — attach receipt photo, supplier and line items with prices. No approval needed.</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Phase</label>
              <select value={phaseId} onChange={(e) => { setPhaseId(e.target.value); setScopeId(""); }}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs" data-testid="prj-mr-phase">
                <option value="">— none —</option>
                {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Scope (optional)</label>
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)} disabled={scopes.length === 0}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs" data-testid="prj-mr-scope">
                <option value="">— none —</option>
                {scopes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Supplier</label>
              <SupplierAutocomplete
                value={supplier}
                onChange={({ name }) => setSupplier(name)}
                testId="prj-mr-supplier"
                placeholder="Home Depot"
              /></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Purchase date</label>
              <input type="date" value={purchasedDate} onChange={(e) => setPurchasedDate(e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] font-bold uppercase text-slate-500">Receipt photo(s) — required</label>
              {photos.length > 0 && (
                <button onClick={scanWithAI} disabled={scanning}
                  data-testid="prj-mr-scan-ai"
                  className="text-[11px] font-bold px-2.5 py-1 rounded bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow inline-flex items-center gap-1 disabled:opacity-60"
                >
                  {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  {scanning ? "Reading..." : (scanResult ? "Re-scan" : "AI-fill from receipt")}
                </button>
              )}
            </div>
            <input type="file" accept="image/*" multiple onChange={onPhotos} className="text-xs" data-testid="prj-mr-photos" />
            {photos.length > 0 && (
              <div className="grid grid-cols-6 gap-2 mt-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="w-full h-16 object-cover rounded border" />
                    <button onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                      className="absolute top-1 right-1 bg-rose-600 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            {scanResult && (
              <div className="mt-2 rounded border border-purple-200 bg-purple-50 px-2.5 py-1.5 text-[11px] text-purple-800" data-testid="prj-mr-scan-result">
                <b className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI extracted</b>
                {" — "}
                Supplier: <b>{scanResult.supplier || "?"}</b>
                {" · "}Date: <b>{scanResult.date || "?"}</b>
                {" · "}Tax: <b className="tabular-nums">{money(scanResult.tax || 0)}</b>
                {" · "}Total: <b className="tabular-nums">{money(scanResult.total || 0)}</b>
                {" · "}Items: <b>{scanResult.items?.length || 0}</b>
                <div className="text-[10px] italic mt-0.5">Review the fields below — approve if they match, edit if not, then Register receipt.</div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold uppercase text-slate-500">Line items</div>
              <div className="text-[10px] text-slate-500 tabular-nums">
                Subtotal: <b className="text-slate-700">{money(subtotal)}</b>
                {" · "}Tax: <input type="number" step="0.01" min="0" value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  data-testid="prj-mr-tax"
                  className="inline-block w-16 rounded border border-slate-300 px-1.5 py-0.5 text-[11px] text-right tabular-nums" />
                {" · "}Total: <b className={`tabular-nums ${mismatch ? "text-rose-600" : "text-slate-900"}`}>{money(total)}</b>
                {mismatch && (
                  <span className="ml-1 text-rose-600 font-bold" title={`AI total: ${money(aiTotal)}`}>
                    ⚠ AI={money(aiTotal)}
                  </span>
                )}
              </div>
            </div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
                <div className="col-span-5">Description</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-1">Unit</div>
                <div className="col-span-2 text-right">Unit $</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-1"></div>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b border-slate-100 items-center" data-testid={`prj-mr-item-${i}`}>
                  <input className="col-span-5 rounded border border-slate-300 px-2 py-1 text-xs"
                    value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })}
                    placeholder="e.g. 2x4 lumber 8ft" data-testid={`prj-mr-item-desc-${i}`} />
                  <input type="number" step="0.01" min="0"
                    className="col-span-1 rounded border border-slate-300 px-1.5 py-1 text-xs text-right tabular-nums"
                    value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} />
                  <input className="col-span-1 rounded border border-slate-300 px-1.5 py-1 text-xs"
                    value={it.unit || "unit"} onChange={(e) => updateItem(i, { unit: e.target.value })} placeholder="unit" />
                  <input type="number" step="0.01" min="0"
                    className="col-span-2 rounded border border-slate-300 px-1.5 py-1 text-xs text-right tabular-nums"
                    value={it.estimated_price} onChange={(e) => updateItem(i, { estimated_price: e.target.value })}
                    data-testid={`prj-mr-item-price-${i}`} />
                  <div className="col-span-2 text-right tabular-nums font-semibold text-xs pt-1">
                    {money((parseFloat(it.quantity) || 0) * (parseFloat(it.estimated_price) || 0))}
                  </div>
                  <button onClick={() => removeItem(i)} disabled={items.length === 1}
                    className="col-span-1 p-1 text-rose-500 rounded hover:bg-rose-50 disabled:opacity-30"
                  ><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <button onClick={addItem} data-testid="prj-mr-add-item"
              className="mt-1 text-[11px] font-semibold text-blue-600 hover:underline inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add item
            </button>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2"
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
              placeholder="e.g. Emergency run for missing screws — cash paid on-site" />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="prj-mr-submit"
            onClick={submit} disabled={busy}
            className="text-xs px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
            Register receipt
          </button>
        </div>
      </div>
    </div>
  );
}
